import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Grid, useMediaQuery } from '@material-ui/core';

// CONSTANTS
import {
  ROUTE_DISCOVER_APP,
  ROUTE_PROFILE_SUBSCRIPTION_DETAIL,
  ROUTE_SUBSCRIPTION,
} from '@/constants/routes';

// UTILITY
import { formatPrice } from '@/utility/currency';
import { analyticsInternalLinks, externalLink, getExternalUrlParams } from '@/utility/analytics';
import { getLink, routeEnhance } from '@/utility/routes';
import { updateUrl } from '@/utility/common';

// HOC
import CheckAuth from '@/hoc/CheckAuth';

// COMPONENTS
import ReactHtml from '@/components/ReactHtml';
import RouterLink from '@/components/Link';

// STYLES
import { Container, Price } from './styles';

const BannerApp = ({ data, link, analyticsLocation }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const { isIframe, unique_id, anonymousId, partner, partnerSettings } = useSelector(
    ({ app, auth, partner: partnerStore }) => ({
      isIframe: app.isIframe,
      unique_id: auth.unique_id,
      anonymousId: auth.anonymousId,
      partner: partnerStore.data,
      partnerSettings: partnerStore.data?.settings || {},
    }),
  );
  const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || anonymousId });
  const appLink = data.list_status ? link : getLink(ROUTE_DISCOVER_APP, { slug: data.unique_slug });
  const hasPartner = partner.partner_slug;
  const isSubscribed = data.user_subscribed;

  let useSubscription = true;

  if (hasPartner) {
    useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
  }

  const subscriptionIsAvailable = useSubscription && data.has_subscription;

  function getPrice(price) {
    return price && Math.ceil(price) !== 0 ? (
      <Typography className="price__price" variant="h5">
        ${formatPrice(price)}
        <span className="p4 text-color--grey">/{t('common.month')}</span>
      </Typography>
    ) : (
      t('common.free')
    );
  }

  const getPriceContent = () => {
    let content = null;

    if (data.deal_price || data.deal_price === 0) {
      content = (
        <>
          <div className="price__old">{getPrice(data.price)}</div>
          <div className="price__deal">{getPrice(data.deal_price)}</div>
        </>
      );
    } else {
      content = getPrice(data.price);
    }

    return content ? <Price className="price">{content}</Price> : null;
  };

  function handleSubscribeOrManage() {
    const isUnsubscribedInIframe = isIframe && !isSubscribed;

    const routeEnhanced = routeEnhance(
      getLink(isSubscribed ? ROUTE_PROFILE_SUBSCRIPTION_DETAIL : ROUTE_SUBSCRIPTION, {
        slug: data.unique_slug,
      }),
      { state: { location: analyticsLocation }, withoutIframe: isUnsubscribedInIframe },
    );

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    if (isUnsubscribedInIframe) {
      window.open(
        `${routeEnhanced.pathname}?${routeEnhanced.search}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      navigate(routeEnhanced);
    }
  }

  function handleVisitSite() {
    const href = updateUrl(data.url, externalUrlParams);

    const properties = {
      location: analyticsLocation,
      href,
      app_id: data.id,
      type: 'app',
      service_name: data.name,
      is_affiliate: !!data.is_affiliate,
      partner: partner?.partner_slug,
      partner_id: partner?.partner_id,
    };

    externalLink(properties);
  }

  return (
    <Container className="banner-app section section--grey">
      <RouterLink className="banner-app__link" to={appLink} location={analyticsLocation} />
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5}>
          <div className="banner-app__left-cont">
            {!!data.thumbnail && (
              <div className="banner-app__logo">
                <img src={data.thumbnail} alt={data.name} />
              </div>
            )}
            <div className="banner-app__details">
              {!!data.trial_period && (
                <Typography className="banner-app__trial text-color--purple">
                  {t('common.trialDaysPeriod', { period: data.trial_period })}
                </Typography>
              )}
              <Typography className="banner-app__name m-b-5" variant={downXs ? 'h4' : 'h3'}>
                {data.name}
              </Typography>
              {getPriceContent()}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={7}>
          <div className="banner-app__right-cont">
            <ReactHtml className="markdown p5 banner-app__desc">{data.desc.trim()}</ReactHtml>
            {subscriptionIsAvailable ? (
              <CheckAuth
                authComponentProps={{
                  type: 'subscription',
                }}
              >
                <Button
                  className="banner-app__btn"
                  color="primary"
                  onClick={handleSubscribeOrManage}
                >
                  {isSubscribed ? t('buttons.manage') : t('buttons.subscribe')}
                </Button>
              </CheckAuth>
            ) : (
              <Button
                className="banner-app__btn"
                component="a"
                href={updateUrl(data.url, externalUrlParams)}
                target="_blank"
                color="primary"
                onClick={handleVisitSite}
              >
                {data.external_button_text ?? t('buttons.visitSite')}
              </Button>
            )}
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

BannerApp.propTypes = {
  data: PropTypes.object.isRequired,
  link: PropTypes.string.isRequired,
  analyticsLocation: PropTypes.string.isRequired,
};

export default React.memo(BannerApp);
